<template>
    <v-layout fill-height>
        <LockedContent :locked="false"/>
        <v-flex xs12>
            <!-- NAVIGATION -->
            <v-breadcrumbs :items="intentsChain">
                <template
                        slot="item"
                        slot-scope="props"
                >
                    <a
                            :class="[props.item.disabled && 'disabled']"
                            @click="navigateChain(props.item)"
                    >{{ props.item.name }}
                    </a>
                </template>
                <v-icon slot="divider">
                    chevron_right
                </v-icon>
            </v-breadcrumbs>
            <!-- NAME AND SAVE -->
            <v-layout wrap row>
                <v-btn
                        icon
                        @click="navigate('*goBack*')"
                >
                    <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <div class="pa-2 font-weight-thin headline" v-if="create === undefined">
                    <v-textarea
                            rows="1"
                            clearable
                            clear-icon="mdi-close"
                            auto-grow
                            style="font-size: 1.2em"
                            class="mt-0 pt-0"
                            v-model="newIntentName">
                    </v-textarea>
                </div>
                <div class="pa-2 font-weight-thin headline" v-else>
                    {{intentName}}
                </div>

                <v-spacer/>
                <v-btn
                        v-if="create === undefined"
                        round
                        color="primary"
                        class="white--text"
                        @click="createIntent()"
                >
                    Create
                    <v-icon
                            right
                            dark
                    >
                        save
                    </v-icon>
                </v-btn>
                <v-btn
                        v-else
                        round
                        color="primary"
                        class="white--text"
                        @click="saveIntentInfo()"
                >
                    {{ $t('training.conversation.save') }}
                    <v-icon
                            right
                            dark
                    >
                        save
                    </v-icon>
                </v-btn>

            </v-layout>

            <!-- UTTERANCES -->
            <v-card class="my-2" :class="{ 'ma-2 rounded': !isMobile() }">
                <v-card-title primary-title>
                    <v-flex grow @click="showUtterances = !showUtterances">
                        <div class="headline">
                            {{ $t('training.conversation.userUtterances') }}
                        </div>
                    </v-flex>
                    <v-flex shrink>
                        <v-btn flat color="primary" @click="addUtterance()">
                            Add User Utterance
                            <v-icon>mdi-plus</v-icon>
                        </v-btn>
                    </v-flex>
                    <v-flex shrink @click="showUtterances = !showUtterances">
                        <v-btn icon>
                            <v-icon>{{ showUtterances ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
                        </v-btn>
                    </v-flex>
                </v-card-title>
                <v-slide-y-transition>
                    <v-card-text
                            v-show="showUtterances"
                            class="pt-0"
                    >
                        <v-layout row wrap>
                            <v-flex xs12 v-for="utterance in utterances"
                                    :key="utterance.utterance">
                                <v-layout row wrap align-center>
                                    <v-flex shrink pa-2>
                                        <v-icon color="pink">
                                            question_answer
                                        </v-icon>
                                    </v-flex>
                                    <v-flex grow v-if="!utterance.editable" @click="editUtterance(utterance)"
                                            v-html="utterance.utterance">
                                    </v-flex>
                                    <v-flex grow v-else>
                                        <v-textarea
                                                @click.stop="getSelection(utterance)"
                                                @keyup.enter="noEdit(utterance)"
                                                rows="1"
                                                clearable
                                                autofocus
                                                clear-icon="mdi-close"
                                                auto-grow
                                                v-model="utterance.utterance"
                                                :value="utterance.utterance">
                                        </v-textarea>
                                    </v-flex>
                                    <v-flex shrink pa-2>
                                        <v-btn icon @click="deleteUtterance(utterance)">
                                            <v-icon color="pink">
                                                delete
                                            </v-icon>
                                        </v-btn>
                                    </v-flex>
                                </v-layout>
                            </v-flex>
                        </v-layout>
                    </v-card-text>
                </v-slide-y-transition>
            </v-card>
            <!-- ANSWERS -->
            <v-card class="my-2" :class="{ 'ma-2 rounded': !isMobile() }">
                <v-card-title primary-title>
                    <v-flex grow @click="showAnswers = !showAnswers">
                        <div class="headline">
                            {{ $t('training.conversation.answers') }}
                        </div>
                    </v-flex>
                    <v-flex shrink>
                        <v-btn flat color="primary" @click="addAnswer()">
                            Add Answer
                            <v-icon>mdi-plus</v-icon>
                        </v-btn>
                    </v-flex>
                    <v-flex shrink>
                        <v-btn icon @click="showAnswers = !showAnswers">
                            <v-icon>{{ showAnswers ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
                        </v-btn>
                    </v-flex>
                </v-card-title>
                <v-slide-y-transition>
                    <v-card-text v-show="showAnswers" class="pt-0">
                        <v-layout row wrap>
                            <v-flex xs12 v-for="answer in answersObject"
                                    :key="answer.text">
                                <v-layout row wrap align-center>
                                    <v-flex shrink pa-2>
                                        <v-icon color="pink">
                                            message
                                        </v-icon>
                                    </v-flex>
                                    <v-flex grow>
                                        <v-textarea
                                                rows="1"
                                                clearable
                                                autofocus
                                                clear-icon="mdi-close"
                                                auto-grow
                                                v-model="answer.text"
                                                :value="answer.text">
                                        </v-textarea>
                                    </v-flex>
                                    <v-flex shrink pa-2>
                                        <v-btn icon @click="deleteAnswer(answer)">
                                            <v-icon color="pink">
                                                delete
                                            </v-icon>
                                        </v-btn>
                                    </v-flex>
                                </v-layout>
                            </v-flex>
                        </v-layout>
                    </v-card-text>
                </v-slide-y-transition>
            </v-card>

            <!-- PLUGINS -->
            <v-card class="my-2" :class="{ 'ma-2 rounded': !isMobile() }">
                <v-card-title primary-title>
                    <v-flex grow>
                        <div class="headline">
                            {{ $t('training.conversation.plugins.header') }}
                        </div>
                    </v-flex>
                </v-card-title>
                <v-card-text class="pt-0">
                    <v-layout row wrap align-center>
                        <v-flex xs12 sm6 md4>
                            <p class="subheading">Plugin: </p>
                        </v-flex>
                        <v-flex xs12 sm6 md8>
                            <v-select
                                    v-model="plugins.selectedPlugin"
                                    v-bind:items="plugins.availablePlugins"
                                    item-text="name"
                                    item-value="name"
                                    return-object
                                    :label="plugins.pluginSelectorOptions.label"
                                    :solo="plugins.pluginSelectorOptions.solo"
                            >

                                <template slot="selection" slot-scope="data">
                                    <v-flex shrink>
                                        <v-avatar v-if="data.item.image" size="25px">
                                            <v-img :src="data.item.image"></v-img>
                                        </v-avatar>
                                        <v-avatar v-else-if="data.item.icon" size="25px">
                                            <v-icon :color="data.item.color">{{ data.item.icon }}</v-icon>
                                        </v-avatar>
                                    </v-flex>
                                    <v-flex grow class='ml-3'>
                                        {{ data.item.name }}
                                    </v-flex>
                                </template>
                                <template slot="item" slot-scope="data">
                                    <v-list-tile-avatar>
                                        <v-img v-if="data.item.image" :src="data.item.image"></v-img>
                                        <v-icon v-else-if="data.item.icon" :color="data.item.color">{{ data.item.icon }}</v-icon>
                                    </v-list-tile-avatar>
                                    <v-list-tile-content>
                                        <v-list-tile-title> {{data.item.name}} </v-list-tile-title>
                                    </v-list-tile-content>
                                </template>
                            </v-select>
                            <!--<SelectImageChooser :options="plugins.pluginSelectorOptions"
                                                :currentSelection="plugins.selectedPlugin"
                                                :items="plugins.availablePlugins"></SelectImageChooser>-->
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                            <p class="subheading mb-1 mr-3">Conversation parameters: </p>
                            <p class="grey--text mb-1 mr-3">The input parameters of the plugin</p>
                        </v-flex>
                        <v-flex xs12 sm6 md8>
                            <v-chip dark v-show="parameters.length > 0"
                                    v-for="parameter in parameters"
                                    close
                                    @input="deleteParameter(parameter)"
                                    @click="openEntityDetail(parameter)"
                                    color="red lighten-1">{{
                                parameter.alias }}
                            </v-chip>
                            <p class="ma-0" v-show="parameters.length === 0">No parameters</p>

                            <v-flex>
                                <v-btn flat color="primary" @click="addParameter()">
                                    Add Conversation Parameter
                                    <v-icon>mdi-plus</v-icon>
                                </v-btn>
                            </v-flex>
                        </v-flex>
                        <v-flex xs12 sm6 md4 mt-3>
                            <p class="subheading mb-1 mr-3">Answer Placeholders: </p>
                            <p class="grey--text mb-1 mr-3">The placeholders that will be replaced by the plugin</p>
                        </v-flex>
                        <v-flex xs12 sm6 md8>
                            <v-chip dark v-show="plugins.answerPlaceholders.length > 0"
                                    v-for="answerPlaceholder in plugins.answerPlaceholders" color="primary">{{
                                answerPlaceholder }}
                            </v-chip>
                            <p class="ma-0" v-show="plugins.answerPlaceholders.length === 0">No placeholders</p>
                        </v-flex>

                    </v-layout>
                </v-card-text>
            </v-card>

            <!-- ATTACHMENTS -->
            <v-card class="my-2" :class="{ 'ma-2 rounded': !isMobile() }">
                <LockedContent
                        :locked="true"
                        :class="{ 'rounded': !isMobile() }"
                />
                <v-card-title
                        primary-title
                        @click="showAttachments = !showAttachments"
                >
                    <v-flex grow>
                        <div class="headline">
                            {{ $t('training.conversation.attachments') }}
                        </div>
                    </v-flex>
                    <v-flex shrink>
                        <v-btn icon>
                            <v-icon>{{ showAttachments ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
                        </v-btn>
                    </v-flex>
                </v-card-title>
                <v-slide-y-transition>
                    <v-card-text
                            v-show="showAttachments"
                            class="pt-0"
                    >
                        <v-list>
                            <v-list-tile
                                    v-for="attachment in attachments"
                                    :key="attachment.title"
                                    v-model="attachment.active"
                                    no-action
                            >
                                <v-list-tile-action>
                                    <v-icon color="pink">
                                        {{ attachment.icon }}
                                    </v-icon>
                                </v-list-tile-action>
                                <v-list-tile-content>
                                    <v-list-tile-title>{{ attachment.title }}</v-list-tile-title>
                                </v-list-tile-content>
                                <v-list-tile-action>
                                    <v-icon color="pink">
                                        delete
                                    </v-icon>
                                </v-list-tile-action>
                            </v-list-tile>
                        </v-list>
                    </v-card-text>
                </v-slide-y-transition>
            </v-card>

        </v-flex>
        <v-snackbar
                v-model="stateToast.activator"
                top
                :timeout=timeout
                color="error"
        >a
            {{ stateToast.message }}
        </v-snackbar>

        <v-dialog
                v-model="assignEntity"
                max-width="450"
                persistent>
            <v-card>
                <v-card-title class="headline">Entity assignment to word: {{selectedText}}</v-card-title>
                <v-card-text>
                    <v-flex >
                        <p class="subheading">Choose entity type:</p>
                    </v-flex>
                    <v-flex>
                        <SelectImageChooser :options="entityOptions"
                                            :currentSelection="selectedType"
                                            :items="entityTypes"></SelectImageChooser>
                    </v-flex>
                    <v-flex >
                        <p class="subheading">Choose variable name:</p>
                        <v-text-field
                                v-model="variableName"
                                regular
                                autofocus
                                color="deep-purple"
                                label="Variable name"></v-text-field>
                    </v-flex>

                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn
                            round
                            color="green"
                            flat
                            @click="confirmEntity"
                    >
                        Confirm
                    </v-btn>
                    <v-btn
                            round
                            color="red"
                            flat
                            @click="assignEntity=false"
                    >
                        Cancel
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
                v-model="entityDetails"
                max-width="450"
                persistent>
            <v-card>
                <v-card-title v-if="newParameter" class="headline">New entity</v-card-title>
                <v-card-title v-else class="headline">Entity details: {{selectedParameter.name}}</v-card-title>
                <v-card-text>
                    <v-flex v-if="newParameter">
                        <v-text-field
                                v-model="selectedParameter.name"
                                regular
                                autofocus
                                color="deep-purple"
                                label="New entity name"></v-text-field>
                    </v-flex>
                    <v-flex v-if="newParameter">
                        <SelectImageChooser :options="entityOptions"
                                            :currentSelection="selectedType"
                                            :items="entityTypes"></SelectImageChooser>
                    </v-flex>
                    <v-flex v-else>
                        <p class="subheading">Type: {{selectedParameter.type}}</p>
                    </v-flex>

                    <v-flex>
                        <v-checkbox v-model="selectedParameter.required" :value="selectedParameter.required" label="Required"></v-checkbox>
                    </v-flex>
                    <v-flex v-if="selectedParameter.required">
                        <p class="subheading">Prompts:</p>
                        <v-text-field
                                v-for="prompt in selectedParameter.prompts"
                                v-model="prompt.text"
                                regular
                                autofocus
                                @keyup.enter="addPrompt"
                                color="deep-purple"
                                label="Prompt text"></v-text-field>
                    </v-flex>

                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn
                            round
                            color="green"
                            flat
                            @click="confirmParameter(selectedParameter)"
                    >
                        Confirm
                    </v-btn>
                    <v-btn
                            round
                            color="red"
                            flat
                            @click="closeEntityDetail"
                    >
                        Cancel
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-layout>


</template>

<script>
    import RouterService from "@/helpers/RouterService";
    import UrlsService from "@/helpers/UrlsService";
    import APILoaderDecoratorService from "@/helpers/APILoaderDecoratorService";
    import TimeService from "@/helpers/TimeService";
    import TextService from "@/helpers/TextService";
    import LockedContent from "@/components/Decorator/LockedContent";
    import SelectImageChooser from "@/components/Inputs/SelectImageChooser";
    import {mapGetters, mapState,} from "vuex";


    export default {
        name: "IntentDetailsTraining",
        components: {SelectImageChooser, LockedContent},
        props: {
            create: Boolean,
            id: String,
        },
        data() {
            return {
                newParameter: false,
                parameters:[],
                selectedParameter:{
                    name: "",
                    type: "",
                    required: false,
                    prompts:[{text: ""}],
                },

                entityRequired: false,
                selectedEntity: {},
                entityDetails: false,
                selectedUtterance: null,
                variableName:"",
                selectedType: null,
                entityOptions: {
                    label: "Choose entity type",
                    solo: true,
                    changeCallback: this.setCurrentType,
                },
                entityTypes:[{name: "@sys.any"},{name: "@sys.email"},{name: "@sys.age"},{name: "@sys.number"},{name: "@sys.geo-country"},{name: "@sys.percentage"},{name: "@sys.geo-state"},{name: "@sys.url"},{name: "@sys.color"},{name: "@sys.language"},{name: "@sys.time"}, {name: "@sys.date"},{name: "@sys.address"},{name: "@sys.location"},{name: "@sys.person"},],
                selectedText:"",
                assignEntity: false,
                timeout: 4000,
                stateToast: {
                    activator: false,
                    message: "",
                    type: ""
                },
                idIntent: "",
                intentName: "",
                answersObject: [{text: ""}],
                utterances: [{
                    name: "",
                    text: null,
                    type: "EXAMPLE",
                    utterance: "",
                    editable: true,
                    parameters: {}
                }],
                newIntentName: "newIntentName",
                intentsChain: [
                    {
                        name: this.$t("training.conversation.intent"),
                        disabled: false,
                        href: "intents",
                    },
                    {
                        name: this.intentName,
                        disabled: false,
                        id: this.id,
                    },
                ],
                showUtterances: false,
                showAnswers: false,
                showAttachments: false,
                showPlugins: false,
                answersOptions: {
                    label: "Set this answers as end of conversation",
                    checkbox: false,
                },
                attachments: [
                    {
                        icon: "attachment",
                        title: "Pizza margherita.pdf",
                    },
                    {
                        icon: "attachment",
                        title: "Pasta alla genovese.docx",
                    },
                ],
                loader: null,
                loadingSaving: false,
                plugins: {
                    selectedPlugin: {
                        name: "Choose a plugin",
                        pluginName: "NULL",
                        pluginNamespace: "NULL",
                        icon: "mdi-diameter-variant",
                        color: ""
                    },
                    pluginSelectorOptions: {
                        label: this.$t('training.conversation.plugins.choosePlugins'),
                        solo: true,
                        changeCallback: this.setCurrentPlugin,
                    },
                    availablePlugins: [
                        {
                            name: "Choose a plugin",
                            pluginName: "NULL",
                            pluginNamespace: "NULL",
                            icon: "mdi-diameter-variant",
                            color: ""
                        },
                    ],
                    conversationParameters: [],
                    answerPlaceholders: [],
                }
            }
        },
        watch: {
            loader() {
                const l = this.loader;
                this[l] = !this[l];
                setTimeout(() => (this[l] = false), 3000);
                this.loader = null;
            },
            "utterances": {
                handler: function (value, oldValue) {
                    this.plugins.conversationParameters = [];
                    if (value) {
                        for (let i = 0; i < value.length; i++) {
                            let parametersInUtterances = null;
                            if (value[i].utterance != null) {
                                parametersInUtterances = TextService.findVariables(value[i].utterance);
                            }
                            if (parametersInUtterances != null) {
                                for (let j = 0; j < parametersInUtterances.length; j++) {
                                    if(!this.plugins.conversationParameters.includes(parametersInUtterances[j])){
                                        this.plugins.conversationParameters.push(parametersInUtterances[j]);
                                    }
                                }
                            }
                        }
                    }
                },
                deep: true
            },
            "answersObject": {
                handler: function (value, oldValue) {
                    this.plugins.answerPlaceholders = [];
                    if (value) {
                        for (let i = 0; i < value.length; i++) {
                            let placeholdersInAnswer = TextService.findPlaceholders(value[i].text);
                            if (placeholdersInAnswer != null) {
                                for (let j = 0; j < placeholdersInAnswer.length; j++) {
                                    this.plugins.answerPlaceholders.push(placeholdersInAnswer[j]);
                                }
                            }
                        }
                    }
                },
                deep: true
            },
        },
        created() {

            this.idIntent = this.$route.params.id;
            // Retrieve here the info of the intent
            // Compute plugins
            let requestUrl = {
                url: UrlsService.plugins,
                timeout: TimeService.secondsToMillis(0),
            };
            let requestBody = {
                interactionData: {
                    pluginsInteractionRequest: {
                        pluginOperation: "getPluginsByScenarioId",
                        plugin: {
                            info: {}
                        }
                    },
                },
            };
            let requestObject = {
                type: "normal",
                urlsInfo: requestUrl,
                data: {
                    data: requestBody,
                },
            };
            let messages = {
                title: "Retrieving plugins",
                loading: "Waiting for plugins...",
                error: "Retrieving failed",
                success: "Plugins list got.",
            };
            let options = {
                loadingType: "notification",
                timeout: TimeService.secondsToMillis(0),
            };
            APILoaderDecoratorService.setLoader(requestObject, messages, options)
                .then(responseSuccess => {
                    let response = responseSuccess;
                    let plugins = response.response.pluginObject.plugins;
                    for (let i = 0; i < plugins.length; i++) {
                        let plugin = {
                            name: plugins[i].info.namespace + " / " + plugins[i].info.name,
                            pluginName: plugins[i].info.name,
                            pluginNamespace: plugins[i].info.namespace,
                            icon: "mdi-puzzle",
                            color: "blue lighten-2"
                        };
                        this.plugins.availablePlugins.push(plugin);
                    }
                })
                .catch(responseError => {

                });
            if (this.create === false) {
                //GET INTENT
                this.getIntent();
            }
        },
        computed: {
            ...mapGetters("UserState", {
                userSettings: "getSettings",
            }),
            ...mapGetters("AppState", {
                globalToastState: "getGlobalToastState",
            }),
            userUtterances: {
                get: function () {
                    return this.utterances
                },
                set: function (newValue) {
                }

            },
        },
        methods: {
            closeEntityDetail(){
                this.entityDetails = false
                this.newParameter = false;
                this.selectedParameter = {
                    name: "",
                    type: "",
                    required: false,
                    prompts: [{text: ""}]
                };
            },

            deleteParameter(param){
                for(let i = 0; i < this.parameters.length; i++){
                    if (this.parameters[i].alias === param.alias) {
                        this.parameters.splice(i, 1);
                    }
                }
            },
            addParameter(){
                this.entityDetails = true;
                this.newParameter = true;
            },
            confirmParameter(param){
                this.entityDetails = false;
                if(this.newParameter){
                    this.parameters.push({alias: param.name, type: this.selectedType.name, required: param.required, prompts: param.prompts})
                    this.newParameter = false;
                }
                else {
                    for (let i=0; i<this.parameters.length; i++){
                        if(this.parameters[i].alias === param.name) {
                            this.parameters[i].required = param.required;
                            this.parameters[i].prompts = param.prompts;
                            break;
                        }
                    }
                }
                this.selectedParameter = {
                    name: "",
                    type: "",
                    required: false,
                    prompts: [{text: ""}]
                };

            },
            openEntityDetail(param){
                this.entityDetails = true;
                this.selectedParameter.name = param.alias;
                this.selectedParameter.type = param.type;
                this.selectedParameter.required = param.required;
                this.selectedParameter.prompts = [];
                for (let i=0; i<param.prompts.length; i++) {
                    this.selectedParameter.prompts.push(param.prompts[i])
                }
                if(param.prompts.length === 0){
                    this.addPrompt();
                }


            },
            addPrompt(){
                this.selectedParameter.prompts.push({text:""})
            },
            confirmEntity(){
                this.assignEntity = false;
                for (let i =0; i< this.utterances.length; i++){
                    if(this.utterances[i].utterance === this.selectedUtterance.utterance) {
                        this.utterances[i].parameters[this.variableName] = {
                            text: this.selectedText,
                            type: this.selectedType.name,
                            alias: this.variableName
                        };
                        break;
                    }
                }
                let present = false;
                for (let i=0; i<this.parameters.length; i++){
                    if(this.parameters[i].name === this.variableName){
                        present = true;
                        break;
                    }
                }
                if(!present){
                    this.parameters.push({alias:this.variableName, type: this.selectedType.name, required: false, prompts:[]})
                }
                this.noEdit(this.selectedUtterance);
            },
            setCurrentType(type) {
                this.selectedType = type;
            },
            getSelection(utterance){
                this.selectedUtterance = utterance;
                this.selectedText = window.getSelection().toString();
                if(this.selectedText !== ""){
                    this.assignEntity = true;
                }
            },
            editUtterance(utterance){
                utterance.editable = true;
                const regex = /\<mark\>(.*?)\<\/mark\>/g;
                let matches = utterance.utterance.match(regex);
                if(matches !== null){
                    for (let i=0; i<matches.length; i++) {
                        let key = matches[i].replace("<mark>","").replace("</mark>","");
                        utterance.utterance = utterance.utterance.replace(`<mark>${key}</mark>`, utterance.parameters[key].text)
                    }
                }
            },
            noEdit(utterance){
                utterance.editable = false;
                for (let key in utterance.parameters){
                    utterance.utterance = utterance.utterance.replace(utterance.parameters[key].text,`<mark>${key}</mark>`)
                }
            },
            getIntentPlugin(){
                let requestUrl = {
                    url: UrlsService.linkIntentPlugin,
                    timeout: TimeService.secondsToMillis(0),
                };

                let requestData = {
                    interactionData: {
                        text: "GET",
                        linkIntentPluginInteractionRequest: {
                            intentName: this.intentName,
                        },
                    },
                };
                let requestObject = {
                    type: "normal",
                    urlsInfo: requestUrl,
                    data: {
                        data: requestData,
                    },
                };
                let messages = {
                    title: "Get plugin of the intent",
                    loading: "Waiting for the plugin...",
                    error: "Cannot get the plugin of the intent...",
                };
                let options = {
                    loadingType: "fullscreen",
                    timeout: TimeService.secondsToMillis(0),
                };

                APILoaderDecoratorService.setLoader(requestObject, messages, options)
                    .then(responseSuccess => {

                        if(responseSuccess.response !== undefined) {
                            let plugin = {
                                name: responseSuccess.response.pluginNamespace + " / " + responseSuccess.response.pluginName,
                                pluginName: responseSuccess.response.pluginName,
                                pluginNamespace: responseSuccess.response.pluginNamespace,
                                icon: "mdi-puzzle",
                                color: "blue lighten-2"
                            };
                            if(plugin.pluginName !== "NULL"){
                                this.setCurrentPlugin(plugin);
                            }
                        }

                    });


            },
            linkIntentPlugin(intentName){
                let requestUrl = {
                    url: UrlsService.linkIntentPlugin,
                    timeout: TimeService.secondsToMillis(0),
                };
                let requestData = {
                    interactionData: {
                        text: "LINK",
                        linkIntentPluginInteractionRequest: {
                            parameters: this.plugins.conversationParameters,
                            placeholders: this.plugins.answerPlaceholders,
                            exceptionPlaceholders:[],
                            intentName:intentName,
                            pluginName: this.plugins.selectedPlugin.pluginName,
                            pluginNamespace:this.plugins.selectedPlugin.pluginNamespace

                        },
                    },
                };
                let requestObject = {
                    type: "normal",
                    urlsInfo: requestUrl,
                    data: {
                        data: requestData,
                    },
                };
                let messages = {
                    title: "Create intent",
                    loading: "Waiting for creating new intent...",
                    error: "Cannot create intent...",
                };
                let options = {
                    loadingType: "fullscreen",
                    timeout: TimeService.secondsToMillis(0),
                };
                APILoaderDecoratorService.setLoader(requestObject, messages, options)
                    .then(responseSuccess => {
                    });


            },
            createIntent() {
                let requestUrl = {
                    url: UrlsService.doIntentOperation,
                    timeout: TimeService.secondsToMillis(0),
                };
                let responses = [];
                let utterances = [];
                for (let i = 0; i < this.utterances.length; i++) {
                    if (this.utterances[i].utterance !== "") {
                        let utt = JSON.parse(JSON.stringify(this.utterances[i]));
                        for (let key in this.utterances[i].parameters){
                            utt.utterance = utt.utterance.replace(`<mark>${key}</mark>`, this.utterances[i].parameters[key].text)
                        }
                        utterances.push(utt);
                    }
                }

                for (let j = 0; j < this.answersObject.length; j++) {
                    if (this.answersObject[j].text !== "") {
                        responses.push(this.answersObject[j].text);
                    }
                }

                for (let k = 0; k < this.parameters.length; k++) {
                    let prompts=[];
                    for (let q=0; q<this.parameters[k].prompts.length; q++){
                        if(this.parameters[k].prompts[q].text !== ""){
                            prompts.push(this.parameters[k].prompts[q].text)
                        }
                    }
                    this.parameters[k].prompts=prompts;

                }

                let requestData = {
                    interactionData: {
                        text: "CREATE",
                        language: this.userSettings.chatbot.language.current.locale,
                        dialogicInteractionRequest: {
                            displayName: this.newIntentName,
                            trainingPhrases: utterances,
                            messageResponses: responses,
                            parameters: this.parameters
                        },
                    },
                };
                let requestObject = {
                    type: "normal",
                    urlsInfo: requestUrl,
                    data: {
                        data: requestData,
                    },
                };
                let messages = {
                    title: "Create intent",
                    loading: "Waiting for creating new intent...",
                    error: "Cannot create intent...",
                };
                let options = {
                    loadingType: "fullscreen",
                    timeout: TimeService.secondsToMillis(0),
                };
                let newIntentName = this.newIntentName;
                if (newIntentName === "" || newIntentName === null) {
                    this.stateToast = {
                        activator: true,
                        message: "Insert the intent name",
                        type: "error"
                    };
                } else if (utterances.length < 1) {
                    this.stateToast = {
                        activator: true,
                        message: "Insert at least one utterance",
                        type: "error"
                    };
                } else {
                    APILoaderDecoratorService.setLoader(requestObject, messages, options)
                        .then(responseSuccess => {
                            this.linkIntentPlugin(this.newIntentName);
                            this.newIntentName = "newIntentName";
                            this.navigate('*goBack*');
                        });
                }


            },
            getIntent() {
                let requestUrl = {
                    url: UrlsService.doIntentOperation,
                    timeout: TimeService.secondsToMillis(0),
                };

                let requestData = {
                    interactionData: {
                        text: "GET",
                        language: this.userSettings.chatbot.language.current.locale,
                        dialogicInteractionRequest: {
                            name: this.idIntent,
                            //displayName: this.intentName
                        },
                    },
                };
                let requestObject = {
                    type: "normal",
                    urlsInfo: requestUrl,
                    data: {
                        data: requestData,
                    },
                };
                let messages = {
                    title: "Get Intent",
                    loading: "Waiting for the intent...",
                    error: "Cannot get the intent...",
                };
                let options = {
                    loadingType: "fullscreen",
                    timeout: TimeService.secondsToMillis(0),
                };
                APILoaderDecoratorService.setLoader(requestObject, messages, options)
                    .then(responseSuccess => {
                        let response = responseSuccess;
                        this.intentName = response.response.intents[0].displayName;
                        this.utterances = [];
                        this.answersObject = [];
                        if (response.response.intents[0].trainingPhrases !== null) {
                            for (let k = 0; k < response.response.intents[0].trainingPhrases.length; k++) {
                                let utt = response.response.intents[0].trainingPhrases[k];
                                utt.editable = false;
                                for (let key in utt.parameters) {
                                    //utt.utterance = utt.utterance.replace(utt.parameters[key].text,"{" + key + "}");
                                    utt.utterance = utt.utterance.replace(utt.parameters[key].text,`<mark>${key}</mark>`);
                                }
                                this.utterances.push(utt);
                            }
                        }
                        if (response.response.intents[0].messages !== null) {
                            for (let j = 0; j < response.response.intents[0].messages.length; j++) {
                                this.answersObject.push({text: response.response.intents[0].messages[j]});
                            }
                        }

                        for (let k=0; k<response.response.intents[0].parameters.length; k++){
                            let p = {alias: response.response.intents[0].parameters[k].alias, type: response.response.intents[0].parameters[k].type, required: response.response.intents[0].parameters[k].required, prompts:[]};
                            for (let h=0; h<response.response.intents[0].parameters[k].prompts.length; h++){
                                p.prompts.push({text: response.response.intents[0].parameters[k].prompts[h]})
                            }
                            this.parameters.push(p)
                        }
                        //GET ASSOCIATED LINK
                        this.getIntentPlugin();
                    });
            },
            isMobile() {
                return this.$vuetify.breakpoint.name === "xs";
            },
            navigate(path) {
                RouterService.goTo(path);
            },
            navigateChain(intent) {
                let path;
                if (intent.name === "Intents") {
                    path = "/admin/training/dialogs";
                } else {
                    path = {name: "IntentTraining", params: {id: intent.href, intentName: intent.name,},};
                }
                this.navigate(path);
            },
            setCurrentPlugin(plugin) {
                this.plugins.selectedPlugin = plugin;
            },

            saveIntentInfo() {
                let requestUrl = {
                    url: UrlsService.doIntentOperation,
                    timeout: TimeService.secondsToMillis(0),
                };
                let utterances = [];
                for (let i = 0; i < this.utterances.length; i++) {
                    if (this.utterances[i].utterance !== "") {
                        let utt = JSON.parse(JSON.stringify(this.utterances[i]));
                        for (let key in this.utterances[i].parameters){
                            utt.utterance = utt.utterance.replace(`<mark>${key}</mark>`, this.utterances[i].parameters[key].text)
                        }
                        utterances.push(utt);

                    }
                }
                let responses = [];
                for (let j = 0; j < this.answersObject.length; j++) {
                    if (this.answersObject[j].text !== "") {
                        responses.push(this.answersObject[j].text);
                    }
                }

                for (let k = 0; k < this.parameters.length; k++) {
                    let prompts=[];
                    for (let q=0; q<this.parameters[k].prompts.length; q++){
                        if(this.parameters[k].prompts[q].text !== ""){
                            prompts.push(this.parameters[k].prompts[q].text)
                        }
                    }
                    this.parameters[k].prompts=prompts;

                }


                let requestData = {
                    interactionData: {
                        text: "UPDATE",
                        language: this.userSettings.chatbot.language.current.locale,
                        dialogicInteractionRequest: {
                            name: this.idIntent,
                            displayName: this.intentName,
                            trainingPhrases: utterances,
                            messageResponses: responses,
                            parameters: this.parameters
                        },
                    },
                };
                let requestObject = {
                    type: "normal",
                    urlsInfo: requestUrl,
                    data: {
                        data: requestData,
                    },
                };
                let messages = {
                    title: "Update Intent intent",
                    loading: "Waiting for updating new intent...",
                    error: "Cannot update intent...",
                };
                let options = {
                    loadingType: "fullscreen",
                    timeout: TimeService.secondsToMillis(0),
                };

                if (utterances.length < 1) {
                    this.stateToast = {
                        activator: true,
                        message: "Insert at least one utterance",
                        type: "error"
                    };
                } else {
                    APILoaderDecoratorService.setLoader(requestObject, messages, options)
                        .then(responseSuccess => {
                            this.linkIntentPlugin(this.intentName);
                        });
                }
            },
            addUtterance() {
                this.showUtterances = true;
                let newUtterance = {
                    name: "",
                    text: null,
                    type: "EXAMPLE",
                    utterance: "",
                    editable: true,
                    parameters: {}
                };

                if (this.utterances.length === 0 || this.utterances[this.utterances.length - 1].utterance !== "")
                    this.utterances.push(newUtterance);
            },
            deleteUtterance(utterance) {
                const index = this.utterances.indexOf(utterance);
                this.utterances.splice(index, 1)
            },
            addAnswer() {
                this.showAnswers = true;
                let newAnswer = {text: ""};
                if (this.answersObject.length === 0 || this.answersObject[this.answersObject.length - 1].text !== "" ) {
                    this.answersObject.push(newAnswer);
                }
            },
            deleteAnswer(answer) {
                const index = this.answersObject.indexOf(answer);
                this.answersObject.splice(index, 1)
            }
        },
    };
</script>

<style lang="scss">
    .custom-loader {
        animation: loader 1s infinite;
        display: flex;
    }

    @-moz-keyframes loader {
        from {
            transform: rotate(0);
        }
        to {
            transform: rotate(360deg);
        }
    }

    @-webkit-keyframes loader {
        from {
            transform: rotate(0);
        }
        to {
            transform: rotate(360deg);
        }
    }

    @-o-keyframes loader {
        from {
            transform: rotate(0);
        }
        to {
            transform: rotate(360deg);
        }
    }

    @keyframes loader {
        from {
            transform: rotate(0);
        }
        to {
            transform: rotate(360deg);
        }
    }
</style>

